import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useIsFeatureSupported, useUserSelfDetails } from '@steelbuy/data-access';
import { Feature, UserRole } from '@steelbuy/ts-shared';
import { ListingDetailsExpiration } from '@steelbuy/ui-domain';
import {
    ButtonCallToAction,
    ButtonPrimary,
    ButtonTertiaryOnLightM,
    FormActionbar,
    LoadingStatus,
} from '@steelbuy/ui-primitive';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { RoutePath } from '../../../router/Routes';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';
import LegalCheckboxes from '../../legal-checkboxes/LegalCheckboxes';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingTable } from '../bulk-listing-table/BulkListingTable';
import { BulkListing, getFilteredHeadings, getListingsGrouped, getStepRoute } from '../bulkListingUtil';

import './BulkListingSummary.scss';

export const BulkListingSummary = () => {
    const { draftId = '' } = useParams();
    const unblockOnSubmit = useRef<boolean>(false);
    const [validity, setValidity] = useState(false);
    const { t } = useTranslation(['translation', 'uiDomain']);
    const navigate = useNavigate();
    const { state } = useLocation();
    const { handleSaveDraft, updateBulkUploadMutation, draftSavedSuccessCallback } = useBulkListingContext();
    const { getValues, reset } = useFormContext();
    const formData = getValues();
    const { fields } = useFieldArray({
        name: 'listings',
    });
    const { data: userData } = useUserSelfDetails();
    const hasSellerRole = userData?.roles.includes(UserRole.SELLER) ?? false;
    const isQualityCategoriesSupported = useIsVendorFeatureSupported('NON_PRIME_CATEGORIES');
    const isDraftSupported = useIsFeatureSupported(Feature.LISTING_DRAFT);

    useEffect(() => {
        if (fields.length === 0) {
            navigate(getStepRoute(1), { replace: true });
        }
        draftSavedSuccessCallback(() => {
            if (unblockOnSubmit.current) {
                reset();
                navigate(RoutePath.CREATE_BULK_LISTING_SUCCESS, { replace: true });
            }
        });
        return () => {
            draftSavedSuccessCallback(() => null);
        };
    }, []);

    if (fields.length === 0) {
        return null;
    }

    const groupedListings = getListingsGrouped(fields as BulkListing[]);

    return (
        <div className="bulk-listing-summary">
            {groupedListings.map((listingGroup) => (
                <div className="bulk-listing-summary-import-group-item" key={listingGroup.groupValue}>
                    <RFQTeaser
                        material={{ ...listingGroup.listings[0], [listingGroup.groupedBy]: listingGroup.groupValue }}
                        showQuality={!isQualityCategoriesSupported}
                    />
                    <BulkListingTable
                        listingsData={listingGroup.listings}
                        tableHeadings={getFilteredHeadings({
                            listings: listingGroup.listings,
                            isSummary: true,
                        })}
                    />
                </div>
            ))}
            <ListingDetailsExpiration expiryDate={formData.expireDate} autoRenew={formData.autoRenew} showAutoRenew />

            {hasSellerRole && <LegalCheckboxes setValidity={setValidity} showSellerTncs />}
            <FormActionbar>
                <ButtonTertiaryOnLightM
                    label={t('uiDomain:common.edit')}
                    onClick={() => navigate(getStepRoute(2, draftId), { state })}
                />

                {hasSellerRole && (
                    <ButtonCallToAction
                        disabled={!validity}
                        loadingStatus={updateBulkUploadMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                        label={t('translation:application.createListingSummary.submitButtonLabel')}
                        onClick={() => {
                            unblockOnSubmit.current = true;
                            handleSaveDraft(draftId, true);
                        }}
                    />
                )}
                {!hasSellerRole && isDraftSupported && (
                    <ButtonPrimary
                        label={t('application.createRFQ.saveAsDraft')}
                        onClick={() => handleSaveDraft(draftId)}
                        loadingStatus={updateBulkUploadMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                    />
                )}
            </FormActionbar>
        </div>
    );
};
